import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { FromConstObject } from "../../../types";
import generators from "../../../shared-utils/noDep/generators";

export const MESSAGE_TYPES = {
  ERROR: "error",
  INFO: "info",
} as const;
type MessageType = FromConstObject<typeof MESSAGE_TYPES>;

type Message = { id: string; message: string; type: MessageType; ttl?: number };
const messagesContext = createContext<{
  markRead: (message: string) => void;
  push: (message: Omit<Message, "id">) => void;
  messages?: Message[];
}>({
  markRead: () => {},
  push: () => {},
});
export const usePushMessage = () => {
  const { push } = useContext(messagesContext);
  return push;
};

export const MessageProvider = ({ children }: ReactElement["props"]) => {
  const [messages, setMessages] = useState<Message[]>([]);

  const markRead = useCallback(
    (id: string) => {
      setMessages((msgs) => msgs.filter((msg) => msg.id !== id));
    },
    [setMessages]
  );

  const push = useCallback(
    (message: Omit<Message, "id">) => {
      const id = generators.randomNumberString(8);
      setMessages((msgs) => [...msgs, { ...message, id }]);
      if (message.ttl) {
        setTimeout(() => markRead(id), message.ttl);
      }
    },
    [setMessages, markRead]
  );

  return (
    <messagesContext.Provider
      value={{
        push,
        markRead,
        messages,
      }}
    >
      <Notification />
      <>{children}</>
    </messagesContext.Provider>
  );
};

const Notification = () => {
  const { messages, markRead } = useContext(messagesContext);
  return messages?.length ? (
    <div className={`fixed max-w-full right-0 p-2 z-[1000]`}>
      {messages.map((message) => (
        <div
          key={message.id}
          className={`my-2 rounded-xl max-w-full w-[500px] px-4 py-2 z-[1000] relative ${
            message?.type === "info"
              ? " bg-primary text-textPrimary "
              : message?.type === "error"
              ? " bg-warning text-textWarning border-textWarning border-solid border-2 rounded "
              : " "
          }`}
        >
          <button
            className="absolute top-2 left-4"
            onClick={() => markRead(message.id)}
          >
            X
          </button>
          <div className="m-5">{message.message}</div>
        </div>
      ))}
    </div>
  ) : null;
};
